import { ShaderChunk } from 'three';

/**
 * Malowany styl dla wszystkich modeli (KayKit, przedmioty z AI, wrogowie, dekoracje) — jedna poprawka shaderów,
 * dzięki której modele z różnych źródeł wyglądają jak z jednej, ręcznie malowanej gry:
 *  1. światło: miękka granica światła i cienia (jak pociągnięcie pędzlem) zamiast gładkiego, „plastikowego” gradientu,
 *     z lekkim zawinięciem światła na stronę cienia;
 *  2. faktura pędzla: delikatne, nieregularne plamy jasności przyklejone do modelu (w układzie modelu,
 *     więc nie pływają przy animacji ani ruchu kamery).
 * Działa na materiały MeshStandard/MeshPhysical (`STANDARD`); efekty, cząsteczki i UI zostają bez zmian.
 * Wywołać raz, przed pierwszą kompilacją shaderów.
 */
export function installPaintedShading(): void {
  if ((ShaderChunk as Record<string, string>).__painted) return;
  (ShaderChunk as Record<string, string>).__painted = 'tak';

  // Zmieniamy tylko światło rozproszone. Odbicia zostają przy oryginalnym dotNL: zawinięte światło
  // po stronie cienia dawało w BRDF_GGX ogromne wartości, czyli iskry i wypalenia w bloomie.
  const diffuse = /reflectedLight\.directDiffuse \+= irradiance \* BRDF_Lambert\(/;
  if (!diffuse.test(ShaderChunk.lights_physical_pars_fragment)) {
    console.warn('[malowany styl] nie znaleziono kodu oświetlenia — inna wersja three.js?');
    return;
  }
  ShaderChunk.lights_physical_pars_fragment = ShaderChunk.lights_physical_pars_fragment.replace(
    diffuse,
    `float dotNLraw = dot( geometryNormal, directLight.direction );
	// Malowane cieniowanie: miękki próg światło/cień + odrobina gładkiego gradientu, żeby bryła nie była płaska.
	float dotNLpaint = smoothstep( -0.08, 0.22, dotNLraw ) * 0.72 + saturate( dotNLraw ) * 0.28;
	reflectedLight.directDiffuse += dotNLpaint * directLight.color * BRDF_Lambert(`,
  );

  // Pozycja w układzie modelu (przed skinningiem) — faktura „przyklejona” do modelu.
  ShaderChunk.common =
    ShaderChunk.common +
    '\nvarying vec3 vPaintPos;\nfloat paintHash( vec3 p ) { p = fract( p * 0.1031 ); p += dot( p, p.zyx + 31.32 ); return fract( ( p.x + p.y ) * p.z ); }\n';
  ShaderChunk.begin_vertex = ShaderChunk.begin_vertex + '\nvPaintPos = transformed;\n';
  ShaderChunk.color_fragment =
    ShaderChunk.color_fragment +
    `
#ifdef STANDARD
	{
		// Faktura pędzla: dwie warstwy szumu wartości, rozciągnięte w pionie jak pociągnięcia.
		// Hash bez sin(): przy dużych współrzędnych sin() traci precyzję i daje migoczące iskry.
		vec3 pp = vPaintPos * vec3( 5.0, 2.2, 5.0 );
		vec3 i = floor( pp ); vec3 f = fract( pp ); f = f * f * ( 3.0 - 2.0 * f );
		float n = mix( mix( mix( paintHash( i ), paintHash( i + vec3( 1, 0, 0 ) ), f.x ),
		                    mix( paintHash( i + vec3( 0, 1, 0 ) ), paintHash( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
		               mix( mix( paintHash( i + vec3( 0, 0, 1 ) ), paintHash( i + vec3( 1, 0, 1 ) ), f.x ),
		                    mix( paintHash( i + vec3( 0, 1, 1 ) ), paintHash( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
		n = clamp( n, 0.0, 1.0 );
		// Jaśniejsze plamy lekko cieplejsze, ciemniejsze lekko chłodniejsze — jak przy malowaniu farbą.
		float k = ( n - 0.5 ) * 0.16;
		diffuseColor.rgb *= 1.0 + k;
		diffuseColor.rgb += vec3( 0.02, 0.01, -0.015 ) * k * 4.0;
	}
#endif
`;
}
